import { config } from "../../config";

const API_BASE = `${config.API_MSWSSTOREMANAGER_BASE_URL}/v2`;

const sessionToken = () =>
	sessionStorage.getItem("sessionToken") || localStorage.getItem("sessionToken");

const json = (method, data) => ({
	method,
	headers: { "Content-Type": "application/json", "X-Session-Token": sessionToken() },
	body: JSON.stringify(data),
});

const request = (method, path, body) =>
	fetch(`${API_BASE}/${path}`, json(method, body)).then(async (r) => {
		const data = await r.json().catch(() => null);
		if (!r.ok) {
			throw new Error(`${method} ${path} ${r.status} ${r.statusText}: ${JSON.stringify(data)}`);
		}
		return data;
	});

const post = (path, body) => request("POST", path, body);
const put = (path, body) => request("PUT", path, body);

/** Mirror AddProducts: include X-Session-Token, no Content-Type, skip non-File values */
const uploadImage = ({ file, imageType, productId, vendorId, variantId }) => {
	// Only upload real files/blobs (skip strings/presigned URLs/nulls)
	if (!(file instanceof File || file instanceof Blob)) return Promise.resolve();

	const fd = new FormData();
	fd.append("bucketname", "microstores.net");
	fd.append("uploadfile", file, file.name || "upload.bin"); // provide a filename
	fd.append("productid", String(productId));
	fd.append("vendorid", String(vendorId));
	fd.append("imageType", imageType);
	fd.append("variantId", String(variantId));

	return fetch(`${API_BASE}/variant-images/uploadProductMediaFile`, {
		method: "POST",
		headers: {
			Accept: "*/*",
			"Cache-Control": "no-cache",
			"X-Session-Token": sessionToken(),
			// DO NOT set Content-Type for FormData
		},
		body: fd,
	}).then(async (res) => {
		if (!res.ok) {
			const text = await res.text().catch(() => "");
			throw new Error(`Image upload (${imageType}) failed ${res.status} ${res.statusText}: ${text}`);
		}
		return res;
	});
};

export async function upsertVariant({ variant, productId, vendorId, ids = {} }) {
	let pricingId = ids.pricingId || null;
	let inventoryId = ids.inventoryId || null;
	let preferenceColorId = ids.preferenceColorId || null;
	let preferenceColorCodeId = ids.preferenceColorCodeId || null;
	let preferenceSizeId = ids.preferenceSizeId || null;

	const isUpdate = !!variant.id;

	// 1) Create the shell if needed
	if (!isUpdate) {
		const { id } = await post(`variant/add?vendorId=${encodeURIComponent(vendorId)}`, {
			productId,
			variantCode: "",
			createdBy: vendorId,
		});
		variant.id = id;
	}

	// 2) Header
	const variantCode =
		variant.variantCode ||
		[variant.color?.trim(), variant.size?.trim()].filter(Boolean).join(" • ") ||
		"Untitled";

	await post(`variant/update?vendorId=${encodeURIComponent(vendorId)}`, {
		id: variant.id,
		productId,
		variantCode,
		updatedBy: vendorId,
		lastUpdated: new Date().toISOString(),
	});

	const vendorQuery = `vendorId=${encodeURIComponent(vendorId)}`;

	// 3) Pricing — POST create, PUT update (same as Add Product)
	const pricingPayload = {
		variantId: variant.id,
		productId,
		barcode: variant.barcode,
		totalPrice: variant.totalPrice,
		pricePerUnit: variant.pricePerUnit,
		units: variant.units,
		quantity: variant.quantity,
		sku: variant.sku,
		contactSupplierFlag: false,
		createdBy: vendorId,
	};
	if (pricingId) {
		await put(`variant-pricing/${encodeURIComponent(pricingId)}?${vendorQuery}`, {
			id: Number(pricingId),
			...pricingPayload,
		});
	} else {
		const created = await post(`variant-pricing?${vendorQuery}`, pricingPayload);
		pricingId = created?.id ?? null;
	}

	// 4) Inventory
	const inventoryPayload = {
		variantId: variant.id,
		productId,
		barcode: variant.barcode,
		sku: variant.sku,
		inStock: variant.currentStock,
		lowStock: variant.lowStock,
		lowStockTrack: variant.lowStockTrack ?? false,
		maxStock: variant.maxStock,
		createdBy: vendorId,
	};
	if (inventoryId) {
		await put(`variant-inventories/${inventoryId}?${vendorQuery}`, {
			id: inventoryId,
			...inventoryPayload,
		});
	} else {
		const created = await post(`variant-inventories?${vendorQuery}`, inventoryPayload);
		inventoryId = created?.id ?? null;
	}

	// 5) Preferences
	const savePref = async (type, value, existingId) => {
		if (!value) return existingId || null;
		const payload = {
			variantId: variant.id,
			productId,
			type,
			value,
			createdBy: vendorId,
		};
		if (existingId) {
			await put(`variant-preferences/${existingId}?${vendorQuery}`, payload);
			return existingId;
		}
		const created = await post(`variant-preferences/add?${vendorQuery}`, payload);
		return created?.id ?? null;
	};

	preferenceColorId = await savePref("color", variant.color, preferenceColorId);
	preferenceColorCodeId = await savePref("colorCode", variant.colorCode, preferenceColorCodeId);
	preferenceSizeId = await savePref("size", variant.size, preferenceSizeId);

	// 6) Images — always map across the 5 slots, skipping non-files inside uploadImage
	const imgTypes = ["primary", "imgSec1", "imgSec2", "imgSec3", "imgSec4"];
	const files = (variant.imageFiles || []).slice(0, 5);
	const padded = [...files]; // if fewer than 5, the rest will be undefined and will be skipped
	while (padded.length < 5) padded.push(undefined);

	await Promise.all(
		imgTypes.map((type, idx) =>
			uploadImage({
				file: padded[idx],
				imageType: type,
				productId,
				vendorId,
				variantId: variant.id,
			})
		)
	);

	return {
		id: variant.id,
		pricingId,
		inventoryId,
		preferenceColorId,
		preferenceColorCodeId,
		preferenceSizeId,
	};
}
